import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, ChevronDown, Loader2, ShieldCheck, Sparkles } from 'lucide-react';
import { ACTION_CATALOG, canonicalArgs, type ActionRisk } from '@esa/shared';
import { ApiError, api } from '../lib/api.js';
import { ago, dateTime } from '../lib/format.js';
import { Badge, Button, Callout, Field, Input, Select, Spinner, useToast } from '../ui/index.js';

/**
 * Actions on a ticket: real changes to real systems (FR-ACT-01..08).
 *
 * NAMED `SystemActions`, not `TicketActions`: that name belongs to the
 * right-click menu that moves and assigns tickets. Those change this
 * system's own records; these change Egnyte.
 *
 * THE SHAPE OF THIS SCREEN IS THE POINT, and the first version had it
 * backwards. It led with a form — choose an action, type a path, type a
 * permission level — and put the offer behind a button beside it. Filling
 * that form in is the whole job the feature exists to save, so it saved
 * nothing: the first person to use it typed the path by hand, found out the
 * capitalisation mattered, and never pressed the button at all.
 *
 * So the offer comes first and arrives unasked. The panel reads the ticket
 * as soon as it is opened and either says what it can do, in one sentence
 * with a yes and a no, or says why it cannot. The form still exists,
 * collapsed, for what the check missed — a fallback, not the front door.
 *
 * Every gate is in the API. A control deciding what is safe from inside the
 * browser has decided nothing.
 */

export interface SystemActionRow {
  id: string;
  action_key: string;
  label: string;
  risk: ActionRisk;
  state:
    | 'proposed'
    | 'awaiting_approval'
    | 'authorized'
    | 'executing'
    | 'succeeded'
    | 'failed'
    | 'cancelled'
    | 'expired';
  args: Record<string, unknown>;
  preview: string;
  rationale: string | null;
  proposed_at: string;
  authorized_at: string | null;
  authorized_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  self_approved: boolean;
  /** Decided by the API for this reader; a lie here permits nothing. */
  can_approve: boolean;
  /** Still waiting on a system owner, whatever `state` reads. */
  needs_approval: boolean;
  /** Who can approve it; empty when nobody owns the system. */
  owner_names?: string[];
  executed_at: string | null;
  error: string | null;
}

interface CatalogRow {
  key: string;
  label: string;
  risk: ActionRisk;
  enabled: boolean;
}

export interface SystemActionsData {
  actions: SystemActionRow[];
  catalog: CatalogRow[];
}

const STATE_TONE: Record<SystemActionRow['state'], 'neutral' | 'warn' | 'ok' | 'danger' | 'info'> =
  {
    proposed: 'info',
    awaiting_approval: 'warn',
    authorized: 'warn',
    executing: 'warn',
    succeeded: 'ok',
    failed: 'danger',
    cancelled: 'neutral',
    expired: 'neutral',
  };

/** In flight outside this system; nothing on this screen can change it. */
const IN_FLIGHT = new Set(['authorized', 'executing']);

/**
 * The hash of what was rendered.
 *
 * Must match the server's `digestOf`: sha256 over canonical JSON, keys
 * sorted. Computed from the arguments this component displayed, so
 * authorizing means "I agree to this sentence" rather than "I agree to
 * whatever that row holds by the time the request lands".
 */
/** "A", "A or B", "A, B or C". */
function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} or ${names[names.length - 1]}`;
}

/** How a system is named to a person. */
const SYSTEM_NAME: Record<string, string> = { egnyte: 'Egnyte' };

/** An argument, as a person reads it -- the catalog's help text is for the model. */
const ARG_LABEL: Record<string, string> = { path: 'Folder', email: 'Person', level: 'Access level' };
const ARG_ORDER = ['path', 'email', 'level'];

const RISK_TEXT: Record<ActionRisk, string> = {
  read: 'Read only · changes nothing',
  low: 'Low risk',
  high: 'High risk',
};

/**
 * One proposal, as a plan: what it is, exactly what it will be done to, why,
 * and what happens between here and done.
 *
 * The person saying yes is agreeing to THESE values, so they are shown as
 * values -- the folder in the monospace it will be sent in -- not buried in
 * a sentence a model wrote. The sentence stays as the summary.
 */
export function ActionPlan({ row }: { row: SystemActionRow }) {
  const def = ACTION_CATALOG[row.action_key];
  const system = def ? (SYSTEM_NAME[def.server] ?? def.server) : null;
  const undoneBy = def?.reversedBy ? ACTION_CATALOG[def.reversedBy]?.label : null;
  // Folder, person, level -- the order the sentence reads in; anything else after.
  const rank = (k: string) => (ARG_ORDER.includes(k) ? ARG_ORDER.indexOf(k) : ARG_ORDER.length);
  const args = Object.entries(row.args ?? {}).sort(([a], [b]) => rank(a) - rank(b));

  const steps: { text: string; done?: string | null }[] = [];
  if (row.needs_approval || row.approved_by_name) {
    steps.push({
      text: `Whoever owns ${system ?? 'the system'} approves it`,
      done: row.approved_by_name
        ? `Approved by ${row.approved_by_name} ${ago(row.approved_at ?? row.proposed_at)}${row.self_approved ? ' (the only owner)' : ''}`
        : null,
    });
  }
  steps.push({ text: `You say yes, and it runs in ${system ?? 'the system'}` });
  steps.push({ text: 'The result is written on this ticket' });

  return (
    <div className="p-3.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1.5 text-2xs font-semibold uppercase tracking-wide text-brand">
          <Sparkles aria-hidden className="h-3.5 w-3.5" />
          Proposed action
        </span>
        <Badge tone={row.risk === 'high' ? 'danger' : row.risk === 'low' ? 'warn' : 'neutral'}>
          {RISK_TEXT[row.risk]}
          {undoneBy ? ' · can be undone' : ''}
        </Badge>
      </div>

      <h3 className="mt-2 text-sm font-semibold text-ink">{def?.label ?? row.label}</h3>
      <p className="mt-0.5 text-xs text-ink-secondary">{row.preview}</p>

      {/* The values being agreed to, as values. */}
      <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-md border border-line-subtle bg-surface-sunken/50 px-3 py-2.5 text-xs">
        {system && (
          <>
            <dt className="text-ink-tertiary">System</dt>
            <dd className="text-ink">{system}</dd>
          </>
        )}
        {args.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-ink-tertiary">{ARG_LABEL[k] ?? k}</dt>
            <dd className={k === 'path' ? 'break-all font-mono text-2xs text-ink' : 'text-ink'}>{String(v)}</dd>
          </div>
        ))}
      </dl>

      {row.rationale && (
        <div className="mt-3">
          <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">Why</p>
          <p className="mt-0.5 text-xs leading-relaxed text-ink-secondary">{row.rationale}</p>
        </div>
      )}

      <div className="mt-3">
        <p className="text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">What happens</p>
        <ol className="mt-1 space-y-1 text-xs text-ink-secondary">
          {steps.map((st, n) => (
            <li key={n} className="flex gap-2">
              <span className="nums flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-tint text-3xs font-semibold text-brand">
                {n + 1}
              </span>
              <span>
                {st.text}
                {st.done && <span className="text-ok"> — {st.done}</span>}
              </span>
            </li>
          ))}
        </ol>
        {undoneBy && <p className="mt-1.5 text-2xs text-ink-tertiary">If it is wrong, it can be reversed with {undoneBy}.</p>}
      </div>
    </div>
  );
}

/** Which system an action changes, so the wait can name it. */
const SYSTEM_OF: Record<string, string> = Object.fromEntries(
  Object.values(ACTION_CATALOG).map((d) => [d.key, SYSTEM_NAME[d.server] ?? d.server]),
);

async function digestOf(args: Record<string, unknown>): Promise<string> {
  const bytes = new TextEncoder().encode(canonicalArgs(args));
  const hash = await crypto.subtle.digest('SHA-256', bytes);
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export function useSystemActions(tenant: string, number: string | number, enabled: boolean) {
  return useQuery({
    queryKey: ['ticket-actions', tenant, number],
    queryFn: () =>
      api.get<SystemActionsData>(`/api/v1/tenants/${tenant}/tickets/${number}/actions`),
    enabled: enabled && Boolean(tenant),
    // A row waiting on the automation server reports back out of band;
    // without a poll the panel sits on "executing" until somebody reloads.
    refetchInterval: (q) =>
      (q.state.data?.actions ?? []).some((a) => IN_FLIGHT.has(a.state)) ? 4000 : false,
  });
}

export function SystemActionsPanel({
  tenant,
  number,
  data,
}: {
  tenant: string;
  /** The route param is a string; the API takes either. */
  number: string | number;
  data: SystemActionsData | undefined;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const invalidate = () =>
    void qc.invalidateQueries({ queryKey: ['ticket-actions', tenant, number] });

  const authorize = useMutation({
    mutationFn: async (row: SystemActionRow) =>
      api.post(`/api/v1/tenants/${tenant}/actions/${row.id}/authorize`, {
        digest: await digestOf(row.args),
      }),
    onSuccess: () => {
      invalidate();
      toast.success('Authorized', 'Handed to the automation server.');
    },
    onError: (e) =>
      // Verbatim. "This proposal changed since it was shown to you" is a
      // sentence the reader has to see exactly as written.
      toast.error(e instanceof ApiError ? e.message : 'That could not be authorized'),
  });

  /**
   * The system owner's sign-off.
   *
   * Sends the same digest `authorize` does, of the arguments THIS component
   * rendered. An owner approves the sentence they read; if the row moved
   * underneath them the server refuses rather than treating the approval as
   * covering something they never saw.
   */
  const approve = useMutation({
    mutationFn: async (row: SystemActionRow) =>
      api.post<{ ok: true; selfApproved: boolean }>(
        `/api/v1/tenants/${tenant}/actions/${row.id}/approve`,
        { digest: await digestOf(row.args) },
      ),
    onSuccess: (r) => {
      invalidate();
      toast.success(
        'Approved',
        r.selfApproved
          ? 'You are the only owner of this system, so this is recorded as your own sign-off.'
          : 'Someone with access can now run it.',
      );
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'That could not be approved'),
  });

  const cancel = useMutation({
    mutationFn: (id: string) => api.post(`/api/v1/tenants/${tenant}/actions/${id}/cancel`, {}),
    onSuccess: () => {
      invalidate();
      toast.success('Dismissed');
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'That could not be dismissed'),
  });

  if (!data) return <Spinner />;

  const available = data.catalog.filter((c) => c.enabled);
  // Waiting on the owner and waiting on an operator are both WAITING. The
  // first version filed `awaiting_approval` under history, which put the one
  // row somebody had to act on below the ones that were already finished.
  const OPEN = new Set(['proposed', 'awaiting_approval']);
  const open = data.actions.filter((a) => OPEN.has(a.state));
  const history = data.actions.filter((a) => !OPEN.has(a.state));

  if (available.length === 0) {
    return (
      <Callout tone="info">
        This group has no actions switched on. A platform admin enables them per group under
        Configuration &rsaquo; Features — nothing here can change another system until they do.
      </Callout>
    );
  }

  return (
    <div className="space-y-4">
      {/* THE OFFER. First, unasked, and phrased as one. */}
      <Offer
        tenant={tenant}
        number={number}
        open={open}
        onAuthorize={(row) => authorize.mutate(row)}
        onApprove={(row) => approve.mutate(row)}
        onCancel={(id) => cancel.mutate(id)}
        approving={approve.isPending}
        authorizing={authorize.isPending}
        cancelling={cancel.isPending}
        onProposed={invalidate}
      />

      {history.length > 0 && (
        <div>
          <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
            Already done here
          </p>
          <ul className="space-y-2">
            {history.map((row) => (
              <li key={row.id} className="rounded-md border border-line-subtle bg-surface p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={STATE_TONE[row.state]}>{row.state}</Badge>
                  <span className="text-2xs text-ink-tertiary">{row.label}</span>
                </div>
                <p className="mt-1.5 text-sm leading-snug text-ink">{row.preview}</p>
                <p className="mt-1 text-3xs text-ink-tertiary">
                  {row.authorized_at && (
                    <>
                      Authorized by{' '}
                      <strong className="text-ink-secondary">
                        {row.authorized_by_name ?? 'someone'}
                      </strong>{' '}
                      {ago(row.authorized_at)}
                    </>
                  )}
                  {row.executed_at && ` · ran ${dateTime(row.executed_at)}`}
                </p>
                {IN_FLIGHT.has(row.state) && (
                  <p className="mt-1.5 flex items-center gap-1.5 text-2xs text-ink-tertiary">
                    <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
                    waiting on the automation server
                  </p>
                )}
                {row.error && <p className="mt-1.5 text-xs text-danger">{row.error}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}

      <ManualProposal tenant={tenant} number={number} catalog={available} onDone={invalidate} />
    </div>
  );
}

/**
 * "Here is what I can do about this ticket."
 *
 * Runs on mount rather than on a button press. The check is idempotent
 * server-side — an open proposal comes back rather than being re-derived —
 * so opening the tab repeatedly costs one model call per ticket, not one per
 * look.
 */
function Offer({
  tenant,
  number,
  open,
  onAuthorize,
  onApprove,
  onCancel,
  approving,
  authorizing,
  cancelling,
  onProposed,
}: {
  tenant: string;
  number: string | number;
  open: SystemActionRow[];
  onAuthorize: (row: SystemActionRow) => void;
  onApprove: (row: SystemActionRow) => void;
  onCancel: (id: string) => void;
  approving: boolean;
  authorizing: boolean;
  cancelling: boolean;
  onProposed: () => void;
}) {
  const [declined, setDeclined] = useState<string | null>(null);
  const asked = useRef(false);

  const ask = useMutation({
    mutationFn: () =>
      api.post<{ proposed: boolean; preview?: string; why?: string }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/actions/suggest`,
        {},
      ),
    onSuccess: (r) => {
      if (r.proposed) {
        setDeclined(null);
        onProposed();
        return;
      }
      setDeclined(r.why ?? 'Nothing in the catalog applies to this ticket.');
    },
    // Quiet on failure: the manual path below still works, and an error
    // toast for a check nobody asked for is noise.
    onError: () => setDeclined(null),
  });

  const fire = ask.mutate;
  useEffect(() => {
    // Once per mount, and only when nothing is already waiting on somebody.
    if (asked.current || open.length > 0) return;
    asked.current = true;
    fire();
  }, [open.length, fire]);

  if (ask.isPending) {
    return (
      <div className="flex items-center gap-2 rounded-md border border-line-subtle bg-surface p-3 text-xs text-ink-secondary">
        <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
        Reading the ticket…
      </div>
    );
  }

  if (open.length > 0) {
    return (
      <ul className="space-y-2">
        {open.map((row) => (
          <li key={row.id} className="overflow-hidden rounded-lg border border-brand-line bg-surface">
            <ActionPlan row={row} />
            <div className="border-t border-line-subtle bg-surface-sunken/60 px-3.5 py-2.5">
            <div className="flex flex-wrap items-center gap-2">
              {/* The row's FACTS, not its state name. A proposal raised
                  before approval existed reads `proposed` and would still be
                  refused; it belongs here, not behind a run button. */}
              {row.needs_approval ? (
                row.can_approve ? (
                  <>
                    <Button onClick={() => onApprove(row)} loading={approving} disabled={approving}>
                      <ShieldCheck aria-hidden className="h-3.5 w-3.5" />
                      Approve
                    </Button>
                    <Button variant="ghost" onClick={() => onCancel(row.id)} disabled={cancelling}>
                      <Ban aria-hidden className="h-3.5 w-3.5" />
                      No
                    </Button>
                    <span className="text-3xs text-ink-tertiary">
                      You own this system. Approving does not run it · raised{' '}
                      {ago(row.proposed_at)}
                    </span>
                  </>
                ) : (
                  <>
                    <Button variant="ghost" onClick={() => onCancel(row.id)} disabled={cancelling}>
                      <Ban aria-hidden className="h-3.5 w-3.5" />
                      Withdraw
                    </Button>
                    {(row.owner_names ?? []).length === 0 ? (
                      <span className="text-2xs text-warn">
                        Nobody owns {SYSTEM_OF[row.action_key] ?? 'this system'} yet, so this cannot
                        be approved. A platform admin names owners under Configuration &rsaquo;
                        Actions.
                      </span>
                    ) : (
                      <span className="text-2xs text-ink-secondary">
                        Waiting on {listNames(row.owner_names ?? [])} ({SYSTEM_OF[row.action_key] ?? 'system'}{' '}
                        owner{(row.owner_names ?? []).length > 1 ? 's' : ''}) to approve it · raised{' '}
                        {ago(row.proposed_at)}
                      </span>
                    )}
                  </>
                )
              ) : (
                <>
                  <Button
                    onClick={() => onAuthorize(row)}
                    loading={authorizing}
                    disabled={authorizing}
                  >
                    <ShieldCheck aria-hidden className="h-3.5 w-3.5" />
                    Yes, do it
                  </Button>
                  <Button variant="ghost" onClick={() => onCancel(row.id)} disabled={cancelling}>
                    <Ban aria-hidden className="h-3.5 w-3.5" />
                    No
                  </Button>
                  <span className="text-3xs text-ink-tertiary">
                    Nothing happens until you say yes · proposed {ago(row.proposed_at)}
                  </span>
                </>
              )}
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="rounded-md border border-line-subtle bg-surface p-3">
      <p className="text-sm text-ink-secondary">
        {declined ?? 'Nothing here can be solved by an action on this desk.'}
      </p>
      <button
        type="button"
        onClick={() => fire()}
        className="mt-1.5 text-2xs text-brand hover:underline"
      >
        Look again
      </button>
    </div>
  );
}

/**
 * The fallback: propose something the check did not find.
 *
 * Collapsed, because reaching for it means the feature has already failed at
 * its job — and because the first version of this panel led with it and
 * taught its first user that this was how the feature worked.
 *
 * Fields are built from the catalog entry: the label is the `argHelp`
 * sentence, and an enum renders as a picker rather than a box whose spelling
 * you have to guess. Typing "full" into a field that wanted "Full" is not
 * something a person should be able to get wrong.
 */
function ManualProposal({
  tenant,
  number,
  catalog,
  onDone,
}: {
  tenant: string;
  number: string | number;
  catalog: CatalogRow[];
  onDone: () => void;
}) {
  const toast = useToast();
  const [showing, setShowing] = useState(false);
  const [key, setKey] = useState(catalog[0]?.key ?? '');
  const [values, setValues] = useState<Record<string, string>>({});

  const def = ACTION_CATALOG[key];
  const shape = (def?.args as { shape?: Record<string, unknown> } | undefined)?.shape ?? {};
  const fields = Object.keys(shape);

  /** A Zod enum's own options, so the picker cannot drift from the schema. */
  const optionsFor = (field: string): string[] | null => {
    const f = (shape as Record<string, { options?: unknown }>)[field];
    return Array.isArray(f?.options) ? (f.options as string[]) : null;
  };

  const propose = useMutation({
    mutationFn: () =>
      api.post<{ id: string; preview: string }>(
        `/api/v1/tenants/${tenant}/tickets/${number}/actions`,
        { actionKey: key, args: values },
      ),
    onSuccess: (r) => {
      setValues({});
      setShowing(false);
      onDone();
      toast.success('Proposed', r.preview);
    },
    onError: (e) =>
      // The server's field-level message names the rule that was broken;
      // "must be a path under /Shared/" is far more use than "invalid".
      toast.error(e instanceof ApiError ? e.message : 'That could not be proposed'),
  });

  if (catalog.length === 0) return null;

  if (!showing) {
    return (
      <button
        type="button"
        onClick={() => setShowing(true)}
        className="flex items-center gap-1.5 text-2xs text-ink-tertiary transition-colors hover:text-ink-secondary"
      >
        <ChevronDown aria-hidden className="h-3.5 w-3.5" />
        Propose something else by hand
      </button>
    );
  }

  return (
    <div className="rounded-md border border-line-subtle bg-surface-sunken p-3">
      <p className="mb-2 text-2xs font-semibold uppercase tracking-wide text-ink-tertiary">
        Propose by hand
      </p>

      <div className="space-y-2.5">
        <Field label="What should happen?">
          <Select
            size="sm"
            value={key}
            onValueChange={(v) => {
              setKey(v);
              setValues({});
            }}
            options={catalog.map((c) => ({ value: c.key, label: c.label }))}
          />
        </Field>

        {fields.map((f) => {
          const options = optionsFor(f);
          return (
            <Field key={f} label={def?.argHelp[f] ?? f}>
              {options ? (
                <Select
                  size="sm"
                  value={values[f] ?? ''}
                  onValueChange={(v) => setValues((prev) => ({ ...prev, [f]: v }))}
                  options={options.map((o) => ({ value: o, label: o }))}
                />
              ) : (
                <Input
                  value={values[f] ?? ''}
                  onChange={(e) => setValues((prev) => ({ ...prev, [f]: e.target.value }))}
                />
              )}
            </Field>
          );
        })}
      </div>

      <div className="mt-3 flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={() => setShowing(false)}>
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={() => propose.mutate()}
          loading={propose.isPending}
          disabled={propose.isPending || fields.some((f) => !values[f]?.trim())}
        >
          Propose
        </Button>
      </div>

      <p className="mt-2 text-3xs leading-relaxed text-ink-tertiary">
        Proposing changes nothing. It records what would be done so somebody can read it and
        decide.
      </p>
    </div>
  );
}
